import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { getErrorMessage } from '@/api/errors'
import { qk } from '@/api/query-keys'
import { cn } from '@/lib/utils'
import { crm2Api, type MigratePreview, type MigrateRun, type MigrateScope } from './api'
import { Section } from './shared'

const SCOPES: Array<[MigrateScope, string, string]> = [
  ['deals', 'People on a deal', 'Only contacts named on an opportunity.'],
  ['companies', 'Also people with a company', 'Adds contacts linked to a company record, a company name or a work email. Recommended.'],
  ['all', 'Everyone', 'Every contact that can be filed under a company.'],
]

type Totals = Omit<MigrateRun, 'offset' | 'next' | 'total' | 'done'>
const empty: Totals = { accounts: 0, contacts: 0, deals: 0, notes: 0, tasks: 0, projects: 0, owners: 0, skipped: 0, errors: [] }

/**
 * The one-time move from GoHighLevel: a dry run first, then the import in small slices with a
 * progress bar. Read-only on GoHighLevel. Running it twice skips what is already here.
 */
export function ImportPanel({ canRun }: { canRun: boolean }) {
  const qc = useQueryClient()
  const [scope, setScope] = useState<MigrateScope>('companies')
  const [preview, setPreview] = useState<MigratePreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [run, setRun] = useState<{ at: number; total: number; totals: Totals; done: boolean } | null>(null)
  // A ref, so the running loop sees Stop pressed mid-run.
  const stopRef = useRef(false)

  const doPreview = async () => {
    setLoading(true)
    try {
      setPreview(await crm2Api.migratePreview(scope))
      setRun(null)
    } catch (e) {
      toast.error(getErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }

  const doImport = async () => {
    if (!preview) return
    stopRef.current = false
    let offset = 0
    let totals = { ...empty, errors: [] as string[] }
    setRun({ at: 0, total: preview.work, totals, done: false })
    try {
      for (let guard = 0; guard < 2000; guard++) {
        const r = await crm2Api.migrateRun({ scope, offset, limit: 5 })
        totals = {
          accounts: totals.accounts + r.accounts,
          contacts: totals.contacts + r.contacts,
          deals: totals.deals + r.deals,
          notes: totals.notes + r.notes,
          tasks: totals.tasks + r.tasks,
          projects: totals.projects + r.projects,
          owners: totals.owners + r.owners,
          skipped: totals.skipped + r.skipped,
          errors: [...totals.errors, ...r.errors],
        }
        offset = r.next
        setRun({ at: r.next, total: r.total, totals, done: r.done })
        if (r.done) break
        if (stopRef.current) break
      }
      toast.success('Import finished')
    } catch (e) {
      toast.error(`Stopped at ${offset}: ${getErrorMessage(e)}. Run it again to carry on; nothing is duplicated.`)
    } finally {
      void qc.invalidateQueries({ queryKey: qk.crm2() })
    }
  }
  const c = preview?.counts
  return (
    <Section title="Import from GoHighLevel">
      <div className="grid gap-4 px-4 py-3 text-sm">
        <p className="text-muted-foreground">A one-time copy of the CRM records in GoHighLevel. Nothing is written to GoHighLevel, and TapCRMv2 keeps no link to it afterwards. Preview first: it reads everything and writes nothing.</p>
        <fieldset className="grid gap-1.5">
          <legend className="mb-1 text-xs font-medium text-muted-foreground">Which contacts come over</legend>
          {SCOPES.map(([k, label, hint]) => (
            <label key={k} className="flex items-start gap-2">
              <input type="radio" name="import-scope" className="mt-1" checked={scope === k} onChange={() => { setScope(k); setPreview(null) }} />
              <span>
                <span className="font-medium">{label}</span> <span className="text-muted-foreground">· {hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => void doPreview()} disabled={!canRun || loading || Boolean(run && !run.done)}>
            {loading ? 'Reading GoHighLevel…' : preview ? 'Preview again' : 'Preview the import'}
          </Button>
          {preview ? (
            <Button size="sm" onClick={() => void doImport()} disabled={!canRun || Boolean(run && !run.done)}>
              Import {preview.work} records
            </Button>
          ) : null}
          {run && !run.done ? (
            <Button size="sm" variant="ghost" onClick={() => { stopRef.current = true }}>
              Stop
            </Button>
          ) : null}
        </div>

        {run ? (
          <div className="grid gap-2 rounded-lg border border-border-soft p-3">
            <div className="flex items-center gap-2">
              <span className="font-medium">{run.done ? 'Done' : 'Importing…'}</span>
              <span className="text-xs text-muted-foreground">
                {run.at} of {run.total}
              </span>
            </div>
            <Progress value={run.total ? (run.at / run.total) * 100 : 0} />
            <p className="text-xs text-muted-foreground">
              {run.totals.accounts} accounts, {run.totals.contacts} contacts, {run.totals.deals} deals, {run.totals.notes} notes, {run.totals.tasks} open tasks, {run.totals.projects} TapPM projects
              {run.totals.owners ? ` · ${run.totals.owners} owners linked` : ''}
              {run.totals.skipped ? ` · ${run.totals.skipped} already here, skipped` : ''}
            </p>
            {run.totals.errors.length ? (
              <details>
                <summary className="cursor-pointer text-xs font-medium text-warning">{run.totals.errors.length} not imported</summary>
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {run.totals.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        ) : null}

        {preview && c ? (
          <div className="grid gap-4">
            <div className="grid gap-2 sm:grid-cols-4">
              {(
                [
                  ['Opportunities', c.opportunities, `${c.byStatus.open} open · ${c.byStatus.won} won · ${c.byStatus.lost + c.byStatus.abandoned} lost`],
                  ['Contacts coming', c.contactsComing, `of ${c.contactsInGhl} in GoHighLevel`],
                  ['Accounts', c.accountsToCreate, `${c.companiesInGhl} company records there`],
                  ['Already imported', c.alreadyImported.deals, 'deals, skipped on a re-run'],
                ] as const
              ).map(([label, n, sub]) => (
                <div key={label} className="rounded-lg bg-muted/60 px-3 py-2">
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="num text-xl font-semibold">{n}</div>
                  <div className="text-tiny text-text-muted">{sub}</div>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto rounded-lg border border-border-soft">
              <table className="w-full text-sm">
                <thead className="bg-muted/60 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Lands in</th>
                    <th className="px-3 py-2 font-medium">As</th>
                    <th className="px-3 py-2 text-right font-medium">Deals</th>
                    <th className="px-3 py-2 font-medium">From GoHighLevel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-soft">
                  {preview.landings.map((l) => (
                    <tr key={`${l.pipeline}${l.stage}${l.status}`}>
                      <td className="px-3 py-2 font-medium">
                        {l.pipeline} · {l.stage}
                      </td>
                      <td className={cn('px-3 py-2', l.status === 'lost' ? 'text-muted-foreground' : l.status === 'won' ? 'text-success' : '')}>{l.status === 'open' ? 'Open' : l.status === 'won' ? 'Won' : 'Lost'}</td>
                      <td className="num px-3 py-2 text-right">{l.count}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{l.from.join('; ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Check title="Owners not matched to anyone in Tapwork" empty="Every owner matched." items={preview.unmatchedOwners.map((o) => `${o.name}${o.email ? ` (${o.email})` : ''}: ${o.deals} deal${o.deals === 1 ? '' : 's'}, imported with no owner`)} />
            <Check title="Stages not in the mapping" empty="Every stage is mapped." items={preview.unknownStages.map((u) => `${u.deal}: ${u.pipeline ?? '?'} · ${u.stage ?? '?'} → ${u.lands}`)} />
            <Check title="Deals with nothing to file them under" empty="Every deal has an account." items={preview.noAccount} />
            <Check title="Accounts with three or more deals (sub-account candidates)" empty="None." items={preview.accountsWithManyDeals.map((a) => `${a.account}: ${a.deals.join(', ')}`)} />
            <Check title="Accounts that may be one enterprise" empty="None found." items={preview.likelyGroups.map((g) => g.names.join(', '))} />
          </div>
        ) : null}
      </div>
    </Section>
  )
}

function Check({ title, items, empty }: { title: string; items: string[]; empty: string }) {
  return (
    <details className="rounded-lg border border-border-soft px-3 py-2" open={items.length > 0 && items.length <= 8}>
      <summary className="cursor-pointer text-sm font-medium">
        {title} <span className={cn('ml-1 rounded-full px-1.5 text-tiny', items.length ? 'bg-warning-soft text-warning' : 'bg-success-soft text-success')}>{items.length}</span>
      </summary>
      {items.length ? (
        <ul className="mt-2 grid gap-1 text-xs">
          {items.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-xs text-muted-foreground">{empty}</p>
      )}
    </details>
  )
}
